import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSmoothScroll } from '../providers/SmoothScrollProvider'

const Navbar = () => {
  const { scrollTo } = useSmoothScroll() ?? {}
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  const navLinks = [
    { text: 'About', href: '#about' },
    { text: 'Projects', href: '#showcase' },
    { text: 'Gallery', href: '#gallery' },
    { text: 'Contact', href: '#contact' },
  ]

  const handleNavClick = (e, href) => {
    e.preventDefault()
    if (scrollTo) {
      scrollTo(href, { offset: -72 })
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' })
    }
    setMobileMenuOpen(false)
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: isScrolled ? '1rem' : '1.5rem',
          left: 0,
          right: 0,
          zIndex: 1000,
          display: 'flex',
          justifyContent: isMobile ? 'flex-end' : 'center',
          alignItems: 'center',
          padding: isMobile ? '0 1rem' : '0',
          pointerEvents: 'none',
        }}
      >
        <motion.nav
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            pointerEvents: 'auto',
            width: isMobile ? 'auto' : 'auto',
            display: 'flex',
            justifyContent: isMobile ? 'flex-end' : 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {/* Desktop Navbar */}
          {!isMobile && (
            <motion.div
              style={{
                background: isScrolled
                  ? 'rgba(26, 25, 23, 0.98)'
                  : 'rgba(26, 25, 23, 0.95)',
                borderRadius: '50px',
                padding: '0.5rem 1.5rem',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: isScrolled
                  ? '0 20px 60px rgba(0, 0, 0, 0.4)'
                  : '0 10px 30px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
              }}
            >
            {navLinks.map((link, index) => (
              <motion.a
                key={link.text}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  color: '#eceae5',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  padding: '0.5rem 1.25rem',
                  borderRadius: '25px',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.08)'
                  e.target.style.color = '#ffffff'
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent'
                  e.target.style.color = '#eceae5'
                }}
              >
                {link.text}
              </motion.a>
            ))}
            <motion.a
              href="https://dashcsv-geos-projects-770b689f.vercel.app/login"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: navLinks.length * 0.1 }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              style={{
                marginLeft: '0.5rem',
                padding: '0.5rem 1.25rem',
                background: '#eceae5',
                color: '#1a1917',
                borderRadius: '25px',
                fontSize: '0.875rem',
                fontWeight: '600',
                textDecoration: 'none',
                boxShadow: 'none',
              }}
            >
              Login
            </motion.a>
          </motion.div>
        )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <motion.button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              whileTap={{ scale: 0.95 }}
              style={{
                marginLeft: 'auto',
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: 'rgba(26, 25, 23, 0.98)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: 0,
                zIndex: 1001,
              }}
            >
              <motion.span
                animate={{
                  rotate: mobileMenuOpen ? 45 : 0,
                  y: mobileMenuOpen ? 6 : 0,
                }}
                transition={{ duration: 0.3 }}
                style={{
                  width: '20px',
                  height: '2px',
                  background: '#eceae5',
                  borderRadius: '2px',
                }}
              />
              <motion.span
                animate={{
                  opacity: mobileMenuOpen ? 0 : 1,
                }}
                transition={{ duration: 0.3 }}
                style={{
                  width: '20px',
                  height: '2px',
                  background: '#eceae5',
                  borderRadius: '2px',
                }}
              />
              <motion.span
                animate={{
                  rotate: mobileMenuOpen ? -45 : 0,
                  y: mobileMenuOpen ? -6 : 0,
                }}
                transition={{ duration: 0.3 }}
                style={{
                  width: '20px',
                  height: '2px',
                  background: '#eceae5',
                  borderRadius: '2px',
                }}
              />
            </motion.button>
          )}
        </motion.nav>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && isMobile && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setMobileMenuOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)',
                zIndex: 999,
              }}
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '280px',
                height: '100vh',
                background: 'rgba(26, 25, 23, 0.98)',
                boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.5)',
                zIndex: 1000,
                padding: '5rem 2rem 2rem',
                display: 'flex',
                flexDirection: 'column',
                borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              {/* macOS-style window controls */}
              <div
                style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem',
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                }}
              >
                <div
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#ff5f56',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                    border: '0.5px solid rgba(0, 0, 0, 0.1)',
                  }}
                />
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#ffbd2e',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                    border: '0.5px solid rgba(0, 0, 0, 0.1)',
                  }}
                />
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#27c93f',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                    border: '0.5px solid rgba(0, 0, 0, 0.1)',
                  }}
                />
              </div>

              {/* Navigation Links */}
              <nav
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  marginTop: '2rem',
                }}
              >
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.text}
                    href={link.href}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    onClick={(e) => handleNavClick(e, link.href)}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      color: '#eceae5',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      fontWeight: '500',
                      padding: '0.875rem 1.25rem',
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.08)'
                      e.target.style.color = '#ffffff'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent'
                      e.target.style.color = '#eceae5'
                    }}
                  >
                    {link.text}
                  </motion.a>
                ))}
              </nav>

              {/* Login Button */}
              <motion.a
                href="https://dashcsv-geos-projects-770b689f.vercel.app/login"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3, delay: navLinks.length * 0.1 }}
                onClick={() => setMobileMenuOpen(false)}
                whileTap={{ scale: 0.98 }}
                style={{
                  marginTop: 'auto',
                  padding: '0.875rem 1.5rem',
                  background: '#eceae5',
                  color: '#1a1917',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  textDecoration: 'none',
                  textAlign: 'center',
                  boxShadow: 'none',
                }}
              >
                Login
              </motion.a>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navbar
